import React, { useEffect, useRef, useState } from 'react';
import './CustomCursor.css';

export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const labelRef = useRef(null);

  // Raw mouse position (dot follows instantly)
  const mouse = useRef({ x: -100, y: -100 });
  // Lagging ring position
  const ring = useRef({ x: -100, y: -100 });
  const rafId = useRef(null);

  const [state, setState] = useState('default'); // default | hover | click | hidden
  const [label, setLabel] = useState('');
  const [onDark, setOnDark] = useState(false);

  useEffect(() => {
    // Hide system cursor globally
    document.documentElement.style.cursor = 'none';

    // ── Smooth ring follow loop ──
    const animate = () => {
      ring.current.x += (mouse.current.x - ring.current.x) * 0.1;
      ring.current.y += (mouse.current.y - ring.current.y) * 0.1;

      if (ringRef.current) {
        ringRef.current.style.transform =
          `translate(${ring.current.x}px, ${ring.current.y}px) translate(-50%, -50%)`;
      }
      rafId.current = requestAnimationFrame(animate);
    };
    rafId.current = requestAnimationFrame(animate);

    // ── Mouse move ──
    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };

      if (dotRef.current) {
        dotRef.current.style.transform =
          `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
      }
      if (labelRef.current) {
        labelRef.current.style.transform =
          `translate(${e.clientX}px, ${e.clientY}px) translate(16px, -50%)`;
      }

      // Detect if cursor is over a dark background element
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el) {
        const bg = getComputedStyle(el).backgroundColor;
        const isDark = isDarkColor(bg) || el.closest('.home__hero, .about__hero, .home__photo-strip, .marquee--dark, .home__work-thumb, .project-card__thumb, .pd__video-wrap');
        setOnDark(!!isDark);
      }
    };

    // ── Hover detection ──
    const onEnter = (e) => {
      const target = e.target.closest('a, button, [data-cursor]');
      if (!target) return;
      const cursorLabel = target.getAttribute('data-cursor-label') || '';
      setLabel(cursorLabel);
      setState('hover');
    };

    const onLeave = (e) => {
      const target = e.target.closest('a, button, [data-cursor]');
      if (!target) return;
      // Only reset if not entering another interactive element
      const related = e.relatedTarget?.closest('a, button, [data-cursor]');
      if (!related) {
        setState('default');
        setLabel('');
      }
    };

    // ── Click ──
    const onDown = () => setState(s => s === 'hover' ? 'hover-click' : 'click');
    const onUp   = () => setState(s => s === 'hover-click' ? 'hover' : 'default');

    // ── Page visibility ──
    const onBlur  = () => setState('hidden');
    const onFocus = () => setState('default');

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseover', onEnter);
    document.addEventListener('mouseout', onLeave);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('mouseup', onUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    return () => {
      document.documentElement.style.cursor = '';
      cancelAnimationFrame(rafId.current);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseover', onEnter);
      document.removeEventListener('mouseout', onLeave);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const cursorClass = [
    'cursor',
    `cursor--${state}`,
    onDark ? 'cursor--on-dark' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Sharp dot — follows instantly */}
      <div ref={dotRef} className={`${cursorClass} cursor__dot`} />

      {/* Lagging ring */}
      <div ref={ringRef} className={`${cursorClass} cursor__ring`} />

      {/* Label that appears on hover when data-cursor-label is set */}
      {label && (
        <div ref={labelRef} className="cursor__label">{label}</div>
      )}
    </>
  );
}

// ── Utility: detect dark background color ──
function isDarkColor(rgb) {
  if (!rgb || rgb === 'transparent' || rgb === 'rgba(0, 0, 0, 0)') return false;
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return false;
  const [r, g, b] = match.map(Number);
  // Perceived luminance
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum < 0.35;
}
